import { use, useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";
import { useDrag } from "@use-gesture/react";

import { ModalsContext } from "lib/modals/use-modals";
import { NavigationContext } from "lib/navigation/use-navigation";
import { SettingsContext } from "lib/settings/use-settings";
import { isTouch } from "lib/utils/device";

const MOVE_THRESHOLD = 5;

function isOnInteractiveElement(e: MouseEvent<HTMLElement>): boolean {
  const el =
    e.target instanceof HTMLElement
      ? e.target
      : (e.target as Node).parentElement;
  return !!(el?.closest("button") || el?.closest("a"));
}

function hasSelection(): boolean {
  return !!window.getSelection()?.toString();
}

export function useContentInteraction() {
  const ref = useRef<HTMLDivElement>(null);
  const dragStartPosition = useRef<{ x: number; y: number } | null>(null);

  const [isChromeVisible, setIsChromeVisible] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);

  const { nextPage, prevPage, totalPages, setDragOffset, page } =
    use(NavigationContext)!;
  const { layoutDirection, turnPageOnClick, setAnimatePageTurns } =
    use(SettingsContext)!;
  const { showUpsell } = use(ModalsContext)!;

  const isTouchDevice = isTouch();

  useEffect(() => {
    const el = ref.current;

    if (el) {
      function handleSelectionChange() {
        const sel = window.getSelection();
        setIsSelecting(!!sel?.toString());
      }

      function handleSelectionEnd() {
        setTimeout(() => {
          const sel = window.getSelection();
          if (!sel?.toString()) {
            setIsSelecting(false);
          }
        }, 100);
      }

      document.addEventListener("selectionchange", handleSelectionChange);
      el.addEventListener("mouseup", handleSelectionEnd);
      el.addEventListener("touchend", handleSelectionEnd);

      return () => {
        document.removeEventListener("selectionchange", handleSelectionChange);
        el.removeEventListener("mouseup", handleSelectionEnd);
        el.removeEventListener("touchend", handleSelectionEnd);
      };
    }
  }, []);

  // Свайп-навигация (только тач)
  const swipeBindings = useDrag(
    ({ active, movement: [mx], tap, swipe: [swipeX], cancel }) => {
      if (!isTouchDevice || tap) return;
      if (layoutDirection !== "horizontal" || totalPages === 0) return;

      if (isSelecting) {
        cancel();
        setDragOffset(0);
        return;
      }

      const atStart = page === 0;
      const atEnd = page === totalPages - 1;

      if (active) {
        let offset = mx;
        if ((atStart && mx > 0) || (atEnd && mx < 0)) {
          offset = mx * 0.3;
        }
        setDragOffset(offset);
      } else {
        setDragOffset(0);
        const threshold = window.innerWidth * 0.2;
        const shouldNavigate = Math.abs(mx) > threshold || swipeX !== 0;

        if (shouldNavigate) {
          setAnimatePageTurns(true);
          if (mx < 0 && !atEnd) {
            nextPage();
          } else if (mx < 0 && atEnd) {
            showUpsell();
          } else if (mx > 0 && !atStart) {
            prevPage();
          }
          setTimeout(() => setAnimatePageTurns(false), 300);
        }
      }
    },
    {
      enabled: isTouchDevice,
      axis: "x",
      filterTaps: true,
      pointer: { touch: true },
      swipe: { distance: 50, velocity: 0.3 },
    }
  );

  function handleMouseDown(e: MouseEvent<HTMLElement>) {
    dragStartPosition.current = { x: e.clientX, y: e.clientY };
  }

  function handleContentClick(e: MouseEvent<HTMLElement>) {
    if (e.defaultPrevented) return;
    if (isOnInteractiveElement(e)) return;
    if (hasSelection()) return;

    if (dragStartPosition.current) {
      const dx = Math.abs(e.clientX - dragStartPosition.current.x);
      const dy = Math.abs(e.clientY - dragStartPosition.current.y);
      if (dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) return;
    }

    if (turnPageOnClick) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const third = rect.width / 3;

      if (x < third) {
        prevPage();
        return;
      }
      if (x > third * 2) {
        nextPage();
        return;
      }
    }

    setIsChromeVisible(v => !v);
  }

  return {
    ref,
    swipeBindings,
    isChromeVisible,
    handleContentClick,
    handleMouseDown,
  };
}
